/**
 * **배치 오버라이드** — 배치 에디터(place.html)가 저장한 「이 조각/프롭은 여기」를 게임과 툴이 같이 읽는다.
 *
 * 저장소는 Poses DO(`/api/poses`)의 `place_<episode>` 이름 하나(에피소드마다 따로). 배치 솔버(placed.ts)를 건드리지 않고
 * 그 위에 얹는 층이다 — 툴에서 옮기면 프리뷰에 바로 먹고, 확정되면 에이전트가 JSON 을 받아
 * `shared/level-<ep>.place.ts` 로 옮겨 적는다(그래야 CI 감사가 같은 자리를 본다).
 *
 * 조각 키: `방/조각#n` — 같은 방 같은 조각 중 PIECES 순서로 n 번째. 에디터가 새로 놓은 것은 `방/조각#new<k>`
 * 로 `added` 에, 지운 것은 키로 `removed` 에 들어간다. 키는 **원래 순서**로 매기고 나서 숨기므로 지워도 밀리지 않는다.
 */
import type { PropDef } from '../../shared/level';
import type { PieceDef } from '../../shared/level-defs';
import { PIECES, PROPS, OUTDOOR, ROOMS, rebuildWalls, type OpeningOverride } from '../../shared/level';
import type { TerrainDeltas } from './terrain';
import { FINISH_BY_ID } from '../../shared/finishes';

export interface PlaceOverrides {
  /** `inside` 는 **에디터에서 서랍·상자 속에 밀어 넣은 것**. `null` 은 「원래 담겨 있던 걸 빼냈다」 */
  pieces: Record<string, { pos: [number, number, number]; rot: number; s?: number; sxyz?: [number, number, number]; rx?: number; rz?: number; inside?: { host: string; part?: string } | null; noclip?: boolean }>;
  props: Record<string, { pos: [number, number, number]; rot: number; s?: number; sxyz?: [number, number, number]; rx?: number; rz?: number; noclip?: boolean }>;
  /** 에디터가 새로 놓은 조각 */
  added?: Record<string, { piece: string; room: string; pos: [number, number, number]; rot: number; s?: number; sxyz?: [number, number, number]; rx?: number; rz?: number; inside?: { host: string; part?: string }; noclip?: boolean }>;
  /** 에디터에서 **새로 낸 문**의 여닫는 문짝. 구멍만 뚫으면 지나갈 수는 있어도 열고 닫을
   *  문이 없다(2026-09-11, Chris: 「상호작용으로 열고 닫고」). 서버도 이 목록을 읽어야
   *  E 키가 먹는다 — 클라이언트만 알면 「없는 물건입니다」로 거절된다. */
  addedProps?: Record<string, { kind: string; room: string; name: string; pos: [number, number, number]; rot: number; half?: number; attach?: string; part?: string }>;
  /** 에디터가 지운 조각(키) */
  removed?: string[];
  /** 창·문 — wallRun 키별로 문 중심·창(중심·반폭) 목록을 통째로 */
  openings?: Record<string, OpeningOverride>;
  /**
   * **방 바닥재** (2026-09-14, Chris 추천 3번). `world.ts` 의 `floorSurface(room)` 이
   * `room.floor` 를 먼저 보므로, 여기서 그 값만 갈아 끼우면 방 하나만 바뀐다.
   * 벽지는 슬롯 자체가 없어 별도 작업으로 둔다.
   */
  rooms?: Record<string, { floor?: string; finish?: string; wall?: string; wains?: string | null }>;
  /** 지형 — 붓으로 바꾼 셀의 변화량(시작값 대비) */
  terrain?: TerrainDeltas;
  /** 나무 — 새로 심은 것 · 뽑은 것(OUTDOOR.trees 의 순번) */
  /** 나무 — 새로 심은 것 · 뽑은 것. `y` 는 밑동 높이다(언덕 위에 심으면 필요하다 — 없으면 게임이 y=0 으로 심는다) */
  trees?: { added: { x: number; z: number; y?: number; h?: number }[]; removed: number[] };
}

/** 에피소드별 저장 이름 — 서버(room.ts)도 `place_<episode>` 를 읽는다 */
export const placeName = (episode: string) => `place_${episode}`;
/** 에디터 작업본 — 「저장」이 여기, 「최종 적용」이 placeName 으로 복사. 게임은 placeName 만 읽는다 */
export const draftName = (episode: string) => `place_${episode}_draft`;

/** /api/poses 를 한 번 받는다. 에디터 부팅이 작업본·최종본·프리셋을 따로따로 네 번 받고 있었다 — 요청마다 0.8초쯤 */
export async function fetchAllPoses(): Promise<Record<string, unknown>> {
  try { return (await (await fetch('/api/poses', { cache: 'no-store' })).json()) as Record<string, unknown>; } catch { return {}; }
}

/** 받아 둔 포즈 저장소에서 작업본을 꺼낸다. 없으면 null */
export function draftFrom(all: Record<string, unknown>, episode: string): PlaceOverrides | null {
  const ov = all[draftName(episode)] as Partial<PlaceOverrides> | undefined;
  if (!ov) return null;
  /* **addedProps 를 빼먹지 마라** (2026-09-23). 에디터로 켠 상호작용(○ 버튼)·새로 낸 문이 여기 사는데
   * 두 fetch 가 이 칸을 버려서, 저장해도 새로고침하면 사라지고 게임엔 한 번도 안 갔다 */
  return { pieces: ov.pieces ?? {}, props: ov.props ?? {}, added: ov.added ?? {}, removed: ov.removed ?? [], openings: ov.openings ?? {}, terrain: ov.terrain, trees: ov.trees, addedProps: ov.addedProps ?? {} };
}

export async function fetchDraftOverrides(episode: string): Promise<PlaceOverrides | null> {
  return draftFrom(await fetchAllPoses(), episode);
}

/** 에디터가 붙이는 확장 필드 — 새로 놓은 조각의 고정 키 · 지운 조각 표시 */
export type PieceExt = PieceDef & { _key?: string; _hidden?: boolean };

/** PIECES 를 순서대로 돌며 부르는 키. `seen` 은 호출자가 한 번의 순회 동안 유지한다 */
export function pieceKey(p: PieceDef, seen: Map<string, number>): string {
  const ext = p as PieceExt;
  if (ext._key) return ext._key;
  const base = `${p.room}/${p.piece}`;
  const n = seen.get(base) ?? 0;
  seen.set(base, n + 1);
  return `${base}#${n}`;
}

export function emptyOverrides(): PlaceOverrides { return { pieces: {}, props: {}, added: {}, removed: [], openings: {} }; }

/** 받아 둔 포즈 저장소에서 최종본(게임이 읽는 것)을 꺼낸다. 없으면 빈 오버라이드 */
export function placeFrom(all: Record<string, unknown>, episode: string): PlaceOverrides {
  const ov = all[placeName(episode)] as Partial<PlaceOverrides> | undefined;
  return { pieces: ov?.pieces ?? {}, props: ov?.props ?? {}, added: ov?.added ?? {}, removed: ov?.removed ?? [], openings: ov?.openings ?? {}, terrain: ov?.terrain, trees: ov?.trees, addedProps: ov?.addedProps ?? {} };
}

export async function fetchPlaceOverrides(episode: string): Promise<PlaceOverrides> {
  return placeFrom(await fetchAllPoses(), episode);
}

/** PIECES·PROPS 항목을 **제자리에서** 고친다 — world.ts·props.ts 가 같은 객체를 읽는다 */
export function applyPlaceOverrides(ov: PlaceOverrides): { pieces: number; props: number; added: number; removed: number } {
  // 창·문이 바뀌었으면 벽을 먼저 다시 짓는다 — 씬을 짓기 전이어야 한다
  if (ov.openings && Object.keys(ov.openings).length) rebuildWalls(ov.openings);
  /* 나무 — `OUTDOOR.trees` 를 제자리에서 고친다(outside.ts 가 같은 배열을 읽는다).
   * 뽑은 것은 **원래 순번**으로 지우므로 뒤 번호가 안 밀리게 내림차순으로 */
  if (ov.trees) {
    const t = OUTDOOR.trees as { x: number; z: number; y?: number; h?: number }[];
    for (const i of [...(ov.trees.removed ?? [])].sort((a, b) => b - a)) if (i >= 0 && i < t.length) t.splice(i, 1);
    for (const a of ov.trees.added ?? []) t.push({ x: a.x, z: a.z, y: a.y, h: a.h });
  }
  /* 방 바닥재 — 조각을 놓기 전에 먹여야 `buildWorld` 가 그 값으로 바닥을 굽는다 */
  if (ov.rooms) {
    for (const r of ROOMS as { id: string; floor?: string; finish?: string }[]) {
      const o = ov.rooms[r.id];
      if (o?.floor) r.floor = o.floor;
      if (o?.finish) r.finish = o.finish;
      if (o?.wall) (r as { wall?: string }).wall = o.wall;
      if (o?.wains !== undefined) (r as { wains?: string | null }).wains = o.wains;
      /* 세트만 고르고 바닥은 안 고른 방 — 세트의 기본 바닥을 깐다(한 벌로 딸려온다는 약속) */
      if (o?.finish && !o.floor) { const fs = FINISH_BY_ID[o.finish]; if (fs) r.floor = fs.floor; }
    }
  }
  const seen = new Map<string, number>();
  const removed = new Set(ov.removed ?? []);
  let np = 0, nr = 0, nh = 0;
  for (const p of PIECES as PieceExt[]) {
    // 키는 **모든** 항목에서 센다 — world.ts 와 순서가 어긋나면 좌표가 옆 물건에 붙는다
    const k = pieceKey(p, seen);
    if (removed.has(k)) { p._hidden = true; nh++; continue; }
    const o = ov.pieces[k];
    if (!o) continue;
    p.pos = [o.pos[0], o.pos[1], o.pos[2]];
    p.rot = o.rot;
    /* **벽을 세우려면 축별로 늘려야 한다** (2026-09-11, Chris: 「유리벽 같은 것도 내가 세울 수
     * 있게」). 기즈모는 원래 축별로 늘어나는데 저장이 `s` 한 숫자라 길이만 늘린 것이 안 남았다.
     * `sxyz` 를 새로 쓰고, 옛 `s` 는 계속 읽는다(균등 배율). */
    if (o.sxyz) (p as { scale3?: [number, number, number] }).scale3 = o.sxyz;
    else if (o.s !== undefined) (p as { scale?: number }).scale = o.s;
    if (o.rx || o.rz) (p as { tilt?: [number, number] }).tilt = [o.rx ?? 0, o.rz ?? 0];
    /* 에디터에서 서랍·상자 속으로 밀어 넣은 것. `null` 은 빼낸 것이므로 원래 `inside` 를 지운다 */
    if (o.inside !== undefined) (p as { inside?: { host: string; part?: string } }).inside = o.inside ?? undefined;
    np++;
  }
  let na = 0;
  for (const [k, a] of Object.entries(ov.added ?? {})) {
    if ((PIECES as PieceExt[]).some((p) => p._key === k)) continue;
    (PIECES as PieceExt[]).push({ piece: a.piece, room: a.room, pos: [a.pos[0], a.pos[1], a.pos[2]], rot: a.rot, inside: a.inside, scale: a.s, scale3: a.sxyz, tilt: a.rx || a.rz ? [a.rx ?? 0, a.rz ?? 0] : undefined, _key: k } as PieceExt);
    na++;
  }
  /* **에디터에서 낸 문의 문짝.** 구멍만 뚫으면 지나갈 수는 있어도 여닫을 것이 없다.
   * 여기서 PROPS 에 밀어 넣으면 buildProps 가 평소 문과 똑같이 문짝을 만든다. */
  for (const [id, a] of Object.entries(ov.addedProps ?? {})) {
    if ((PROPS as PropDef[]).some((d) => d.id === id)) continue;
    (PROPS as PropDef[]).push({ id, kind: a.kind as PropDef['kind'], room: a.room, name: a.name,
      pos: [a.pos[0], a.pos[1], a.pos[2]], rot: a.rot, half: a.half, attach: a.attach, part: a.part } as PropDef);
    na++;
  }
  for (const d of PROPS as PropDef[]) {
    const o = ov.props[d.id];
    if (!o) continue;
    d.pos = [o.pos[0], o.pos[1], o.pos[2]];
    d.rot = o.rot;
    if (o.sxyz) d.scale3 = o.sxyz;
    else if (o.s !== undefined) d.scale = o.s;
    if (o.rx || o.rz) d.tilt = [o.rx ?? 0, o.rz ?? 0];
    nr++;
  }
  return { pieces: np, props: nr, added: na, removed: nh };
}
